import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface EffectiveConfig {
  enabled: boolean;
  pagination: { defaultPageSize: number; maxPageSize: number; maxMapPoints: number; clusterZoomThreshold: number };
  gps: { accuracyThresholdMeters: number; coordinateReuseThreshold: number };
  duplicate: { radiusMeters: number; timeWindowHours: number };
  dryArea: { months: number; coverageMinAds: number; neighbourFactor: number };
  density: { gridPrecision: number };
  mvRefreshCron: string;
  map: { provider: string; configuredProvider: string; usingFreeFallback: boolean };
}

/** §9/§21 administration — effective config + materialized-view refresh. */
export function GeoAdminScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [config, setConfig] = useState<EffectiveConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: EffectiveConfig }>('/v1/geo-intel/admin/config', { token })
      .then((r) => { if (alive) { setConfig(r.data); setError(null); } })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiError && e.status === 403
          ? t('m16report.admin.forbidden')
          : (e as Error).message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const refresh = async (): Promise<void> => {
    setBusy(true);
    setStatus(null);
    setError(null);
    try {
      const r = await apiRequest<{ data: { refreshedAt: string } }>(
        '/v1/geo-intel/admin/refresh-materialized-views',
        { method: 'POST', token },
      );
      setStatus(t('m16report.admin.refreshed', { at: at(r.data.refreshedAt) }));
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403
        ? t('m16report.admin.refresh.forbidden')
        : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="geo-admin">
      <h1>{t('m16report.admin.title')}</h1>
      {error && <p role="alert" aria-label="admin-error">{error}</p>}
      {status && <p aria-label="admin-status">{status}</p>}
      <Button variant="secondary" type="button" aria-label="refresh-mvs" disabled={busy} onClick={refresh}>
        {busy ? t('m16report.admin.refreshing') : t('m16report.admin.refresh')}
      </Button>
      {loading ? <p>{t('common.loading')}</p> : config && (
        <section aria-label="effective-config">
          <h2>{t('m16report.admin.config.title')}</h2>
          <ul>
            <li aria-label="cfg-enabled">{t('m16report.admin.config.enabled', { value: String(config.enabled) })}</li>
            <li aria-label="cfg-map">{t('m16report.admin.config.map', { provider: config.map.provider, configured: config.map.configuredProvider, fallback: config.map.usingFreeFallback ? t('m16report.admin.config.map.fallback') : '' })}</li>
            <li aria-label="cfg-gps">{t('m16report.admin.config.gps', { accuracy: config.gps.accuracyThresholdMeters, reuse: config.gps.coordinateReuseThreshold })}</li>
            <li aria-label="cfg-duplicate">{t('m16report.admin.config.duplicate', { radius: config.duplicate.radiusMeters, window: config.duplicate.timeWindowHours })}</li>
            <li aria-label="cfg-dry">{t('m16report.admin.config.dry', { months: config.dryArea.months, minAds: config.dryArea.coverageMinAds, factor: config.dryArea.neighbourFactor })}</li>
            <li aria-label="cfg-density">{t('m16report.admin.config.density', { precision: config.density.gridPrecision })}</li>
            <li aria-label="cfg-page">{t('m16report.admin.config.page', { size: config.pagination.defaultPageSize, max: config.pagination.maxPageSize, points: config.pagination.maxMapPoints })}</li>
            <li aria-label="cfg-cron">{t('m16report.admin.config.cron', { cron: config.mvRefreshCron })}</li>
          </ul>
          <p>{t('m16report.admin.config.note')}</p>
        </section>
      )}
      <StageColoursEditor />
    </main>
  );
}

interface StageColour { stageCode: string; colourHex: string }

/** The colour each activity stage is drawn in on the map (t641, per tenant). */
function StageColoursEditor(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [colours, setColours] = useState<StageColour[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: { stages: StageColour[] } }>('/v1/geo-intel/activity-legend', { token })
      .then((r) => { if (alive) setColours(r.data.stages.map(({ stageCode, colourHex }) => ({ stageCode, colourHex }))); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const save = async (): Promise<void> => {
    if (!colours) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const r = await apiRequest<{ data: StageColour[] }>('/v1/geo-intel/admin/stage-colours', {
        method: 'PUT', token, body: { styles: colours },
      });
      setColours(r.data.map(({ stageCode, colourHex }) => ({ stageCode, colourHex })));
      setStatus(t('m16report.admin.stageColours.saved'));
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403
        ? t('m16report.admin.forbidden')
        : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="stage-colours">
      <h2>{t('m16report.admin.stageColours.title')}</h2>
      <p>{t('m16report.admin.stageColours.note')}</p>
      {error && <p role="alert" aria-label="stage-colours-error">{error}</p>}
      {status && <p role="status" aria-label="stage-colours-status">{status}</p>}
      {colours && (
        <>
          <ul>
            {colours.map((c) => (
              <li key={c.stageCode}>
                <label>
                  <input
                    type="color"
                    aria-label={`stage-colour-${c.stageCode}`}
                    value={c.colourHex}
                    disabled={busy}
                    onChange={(e) => setColours(colours.map((x) => x.stageCode === c.stageCode
                      ? { ...x, colourHex: e.target.value.toUpperCase() } : x))}
                  />{' '}
                  {t(`m16report.stage.${c.stageCode}`)}
                </label>
              </li>
            ))}
          </ul>
          <Button variant="secondary" type="button" aria-label="save-stage-colours" disabled={busy} onClick={save}>
            {t('m16report.admin.stageColours.save')}
          </Button>
        </>
      )}
    </section>
  );
}
